
import axios, { clearAuthStorage } from "./axiosinstance";
import { APIS, BASE_URL } from "../utils/apifactory";
import { showAppSnackbar } from "../utils/appSnackbar";
import {
    normalizeApiResponse,
    isApiSuccess,
    getApiData,
    getApiEntity,
    getApiMessage,
    extractApiList,
    getApiPageMeta,
    getUploadedFileUrl as extractUploadedFileUrl,
    normalizeSelfieItem,
    normalizeSelfieList,
    normalizeSelfieLikeResponse,
    normalizeProductLikeResponse,
    normalizeSelfieSuggestions,
    normalizeProductItem,
    normalizeLikedProductList,
    getSelfieAffiliateId,
    getCreatorDisplayName,
    normalizeCartResponse,
    normalizeUserProfile,
    normalizeUserProfileStats,
    normalizePurchasedProductList,
    mapSelfieProductRef,
    buildBrandCache,
    loadBrandCache,
    saveBrandCache,
} from "../utils/apiResponse";

export {
    normalizeApiResponse,
    isApiSuccess,
    getApiData,
    getApiEntity,
    getApiMessage,
    extractApiList,
    normalizeCartResponse,
    formatPriceEur,
    getPaymentClientSecret,
} from "../utils/apiResponse";


const authHeaders = (token) => ({
    headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: token } : {}),
    },
});

const handleAuthResponse = (callBack, error) => (res) => callBack(res.data);

const handleAuthError = (callBack, error) => (err) => {
    console.error("API Error:", err);
    if (err.response) {
        callBack(err.response.data);
    } else if (error) {
        error(err);
    }
};

const buildUserFromPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.user && typeof payload.user === 'object') {
        const user = payload.user;
        return {
            ...user,
            picture: user.picture || user.profile_image || null,
            full_name:
                user.full_name ||
                `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                user.username ||
                '',
        };
    }

    const { access_token, refresh_token, token, ...userFields } = payload;
    if (!userFields._id && !userFields.email) {
        return null;
    }

    return {
        ...userFields,
        picture: userFields.picture || userFields.profile_image || null,
        full_name:
            userFields.full_name ||
            `${userFields.first_name || ''} ${userFields.last_name || ''}`.trim() ||
            userFields.username ||
            '',
    };
};

const extractAuthPayload = (response) => {
    const data = getApiData(response);
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        if (data.access_token || data.token || data.user) {
            return data;
        }
        if (data.data && typeof data.data === 'object') {
            return data.data;
        }
    }

    return getApiEntity(response) || response;
};

export const normalizeAuthResponse = (response) => {
    const normalized = normalizeApiResponse(response);
    const payload = extractAuthPayload(response);

    const accessToken =
        payload?.access_token ||
        payload?.token ||
        payload?.user?.access_token ||
        response?.access_token ||
        response?.token ||
        response?.data?.access_token;

    const refreshToken =
        payload?.refresh_token ||
        payload?.user?.refresh_token ||
        response?.refresh_token ||
        response?.data?.refresh_token;

    const user =
        buildUserFromPayload(payload) ||
        buildUserFromPayload(response?.data) ||
        buildUserFromPayload(response);

    return {
        success: normalized.success !== false && !!accessToken,
        message: normalized.message || response?.message || '',
        access_token: accessToken,
        refresh_token: refreshToken,
        user,
        address_id: payload?.address_id || user?.address_id || response?.address_id,
    };
};

export const isAuthSuccess = (response) => {
    const normalized = normalizeAuthResponse(response);
    return normalized.success === true && !!normalized.access_token;
};

export const storeLoginResponse = (response) => {
    if (!response?.success) {
        return false;
    }

    const data = response?.data && typeof response.data === 'object' ? response.data : null;
    const accessToken = data?.access_token || response?.access_token;
    const refreshToken = data?.refresh_token || response?.refresh_token;

    if (!accessToken) {
        return false;
    }

    localStorage.setItem('btoken', accessToken);

    if (refreshToken) {
        localStorage.setItem('rtoken', refreshToken);
    }

    const userSource = data || response;
    const { access_token, refresh_token, token, ...userFields } = userSource;

    if (userFields._id || userFields.email) {
        localStorage.setItem('userData', JSON.stringify({
            ...userFields,
            picture: userFields.profile_image || userFields.picture || null,
        }));

        if (userFields._id) {
            localStorage.setItem('userId', userFields._id);
        }
    }

    window.dispatchEvent(new Event('auth-session-changed'));
    return true;
};

export const persistAuthSession = (response) => {
    if (storeLoginResponse(response)) {
        const data = response.data;
        return {
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            user: data,
            address_id: data.address_id,
        };
    }

    const { access_token, refresh_token, user, address_id } = normalizeAuthResponse(response);

    if (access_token) {
        localStorage.setItem('btoken', access_token);
    }
    if (refresh_token) {
        localStorage.setItem('rtoken', refresh_token);
    }
    if (address_id) {
        localStorage.setItem('addressId', address_id);
    }
    if (user) {
        localStorage.setItem('userData', JSON.stringify(user));
        if (user._id) {
            localStorage.setItem('userId', user._id);
        }
    }

    window.dispatchEvent(new Event('auth-session-changed'));

    return { access_token, refresh_token, user, address_id };
};

export const clearAuthSession = () => {
    clearAuthStorage();
};

export const getStoredUser = () => {
    try {
        return JSON.parse(localStorage.getItem('userData') || 'null');
    } catch {
        return null;
    }
};

export const syncStoredUserProfile = (user) => {
    if (!user || typeof user !== 'object') return null;

    const normalized = {
        ...user,
        picture: user.picture || user.profile_image || null,
        profile_image: user.profile_image || user.picture || null,
    };

    localStorage.setItem('userData', JSON.stringify(normalized));
    if (normalized._id) localStorage.setItem('userId', normalized._id);

    return normalized;
};

export const notifyProfileUpdated = (user) => {
    const normalized = syncStoredUserProfile(user);
    window.dispatchEvent(new Event('profile-updated'));
    return normalized;
};

export const persistAffiliateId = (selfieOrCreator) => {
    const affiliateId = getSelfieAffiliateId(selfieOrCreator);
    if (affiliateId) {
        localStorage.setItem('affiliate_id', affiliateId);
    }

    const user =
        selfieOrCreator?.user ||
        (selfieOrCreator?.user_id && typeof selfieOrCreator.user_id === 'object'
            ? selfieOrCreator.user_id
            : null);
    const picture = user?.picture || user?.profile_image || null;
    if (picture) {
        localStorage.setItem('pimage', picture);
    }
    const displayName = getCreatorDisplayName(user || selfieOrCreator);
    if (displayName) {
        localStorage.setItem('creator_display_name', displayName);
    }
    const userId = user?._id || affiliateId;
    if (userId) {
        localStorage.setItem('creator_user_id', userId);
    }

    return affiliateId;
};

export const getAffiliateId = () => localStorage.getItem('affiliate_id');

export const getPersistedCreator = () => ({
    picture: localStorage.getItem('pimage') || '',
    name: localStorage.getItem('creator_display_name') || '',
    userId: localStorage.getItem('creator_user_id') || '',
});

export const checkUsernameAvailabilityApi = (username, callBack, error) => {
    axios.post(APIS.CHECK_USERNAME, { username }, authHeaders())
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const signupApi = (data, callBack, error) => {
    axios.post(APIS.SIGNUP, data, authHeaders())
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const signupVerifyOtpApi = (data, callBack, error) => {
    axios.post(APIS.SIGNUP_VERIFY_OTP, data, authHeaders())
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const signupResendOtpApi = (email, callBack, error) => {
    axios.post(APIS.SIGNUP_RESEND_OTP, { email }, authHeaders())
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const loginApi = (email, password, callBack, error) => {
    axios.post(APIS.LOGIN, { email, password }, authHeaders())
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const googleLoginApi = (data, callBack, error) => {
    axios.post(APIS.GOOGLE_LOGIN, data, authHeaders())
        .then((res) => {
            const response = res.data;
            storeLoginResponse(response);
            callBack(response);
        })
        .catch(handleAuthError(callBack, error));
};

export const appleLoginApi = (data, callBack, error) => {
    axios.post(APIS.APPLE_LOGIN, data, authHeaders())
        .then((res) => {
            const response = res.data;
            storeLoginResponse(response);
            callBack(response);
        })
        .catch(handleAuthError(callBack, error));
};

export const guestLoginApi = (email, password, callBack, error) => {
    axios.post(APIS.GUEST_LOGIN, { email, password }, authHeaders())
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const getAuthMeApi = (token, callBack, error) => {
    axios.get(APIS.AUTH_ME, authHeaders(token))
        .then((res) => {
            const raw = res.data;
            const user = normalizeUserProfile(raw);

            if (isApiSuccess(raw) && user) {
                syncStoredUserProfile(user);
            }

            callBack({
                ...normalizeApiResponse(raw),
                success: isApiSuccess(raw),
                message: getApiMessage(raw),
                data: user,
                user,
            });
        })
        .catch(handleAuthError(callBack, error));
};

export const logoutApi = (token, callBack, error) => {
    axios.post(APIS.LOGOUT, null, authHeaders(token))
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const registerDeviceApi = (token, data, callBack, error) => {
    axios.post(APIS.REGISTER_DEVICE, data, authHeaders(token))
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const forgotPasswordApi = (email, callBack, error) => {
    axios.post(APIS.FORGOT_PASSWORD, { email }, authHeaders())
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const forgotPasswordVerifyOtpApi = (data, callBack, error) => {
    axios.post(APIS.FORGOT_PASSWORD_VERIFY_OTP, data, authHeaders())
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const resetPasswordApi = (data, callBack, error) => {
    axios.post(APIS.RESET_PASSWORD, data, authHeaders())
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const registerApi = signupApi;

const extractList = extractApiList;

const toApiPage = (page) => {
    if (page === '' || page === undefined || page === null) return 1;
    return Number(page) + 1;
};

const appendQueryParams = (baseUrl, params = {}) => {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            query.append(key, String(value));
        }
    });

    const queryString = query.toString();
    if (!queryString) return baseUrl;

    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${queryString}`;
};

const paginatedGet = (url, token, callBack, error, mapItems = (items) => items) => {
    axios.get(url, authHeaders(token))
        .then((res) => callBack(mapItems(extractApiList(res.data))))
        .catch(handleAuthError(callBack, error));
};

const entityGet = (url, token, callBack, error, mapEntity = (entity) => entity) => {
    axios.get(url, authHeaders(token))
        .then((res) => callBack(mapEntity(getApiEntity(res.data))))
        .catch(handleAuthError(callBack, error));
};

// Products
export const getLinkedProductsApi = (token, { page = 1, perPage = 100 } = {}, callBack, error) => {
    paginatedGet(
        `${APIS.GET_LINKED_PRODUCTS}?page=${page}&perPage=${perPage}`,
        token,
        callBack,
        error,
        (items) => (Array.isArray(items) ? items.map((item) => mapSelfieProductRef(item, true)).filter(Boolean) : [])
    );
};

export const getProductsListApi = (token, { page = 1, perPage = 100, brand_id = '' } = {}, callBack, error) => {
    let url = `${APIS.GET_PRODUCTS_LIST}?page=${page}&perPage=${perPage}`;
    if (brand_id) url += `&brand_id=${brand_id}`;

    axios.get(url, authHeaders(token))
        .then((res) => {
            const rawItems = extractApiList(res.data);
            const items = Array.isArray(rawItems)
                ? rawItems.map(normalizeProductItem).filter(Boolean)
                : [];
            callBack(items, getApiPageMeta(res.data));
        })
        .catch(handleAuthError(() => callBack([], null), error));
};

export const fetchBrandCatalogCache = (token, callBack, error) => {
    let cache = loadBrandCache();
    let pending = 2;

    const finish = () => {
        pending -= 1;
        if (pending <= 0) {
            saveBrandCache(cache);
            callBack(cache);
        }
    };

    const mergeList = (list) => {
        cache = { ...cache, ...buildBrandCache(Array.isArray(list) ? list : []) };
        finish();
    };

    paginatedGet(
        `${APIS.GET_LINKED_PRODUCTS}?page=1&perPage=200`,
        token,
        mergeList,
        () => finish(),
        (items) => items
    );

    paginatedGet(
        `${APIS.GET_PRODUCTS_LIST}?page=1&perPage=200`,
        token,
        mergeList,
        () => finish(),
        (items) => items
    );
};

export const enrichBrandCacheFromSelfie = (token, selfieId, callBack, error) => {
    if (!token || !selfieId) {
        if (callBack) callBack(loadBrandCache());
        return;
    }

    getSelfieApi(token, selfieId, (selfie) => {
        const cache = {
            ...loadBrandCache(),
            ...buildBrandCache(selfie?.items_id || []),
            ...buildBrandCache(selfie?.linked_products || []),
            ...buildBrandCache(selfie?.similar_products || []),
            ...buildBrandCache(selfie?.recommended_products || []),
        };
        saveBrandCache(cache);
        if (callBack) callBack(cache);
    }, error);
};

export const getProductApi = (token, productId, callBack, error) => {
    entityGet(`${APIS.GET_PRODUCT}${productId}`, token, callBack, error, normalizeProductItem);
};

export const getLikedProductsApi = (token, { page = 1, perPage = 15 } = {}, callBack, error) => {
    paginatedGet(
        `${APIS.GET_LIKED_PRODUCTS}?page=${page}&perPage=${perPage}`,
        token,
        callBack,
        error,
        (items) => normalizeLikedProductList(items)
    );
};

// Selfies
export const getCreatorSelfiesApi = (
    token,
    { page = 1, perPage = 100, search = '', user_id = '', brand_id = '' } = {},
    callBack,
    error
) => {
    const url = appendQueryParams(APIS.GET_CREATOR_SELFIES, {
        page,
        perPage,
        search,
        user_id,
        brand_id,
    });
    paginatedGet(url, token, callBack, error, normalizeSelfieList);
};

export const getSelfieSuggestionsApi = (
    token,
    { search = '', page = 1, perPage = 10 } = {},
    callBack,
    error
) => {
    const url = appendQueryParams(APIS.GET_SELFIE_SUGGESTIONS, {
        search,
        page,
        perPage,
    });

    axios.get(url, authHeaders(token))
        .then((res) => {
            if (!isApiSuccess(res.data)) {
                callBack([]);
                return;
            }
            callBack(normalizeSelfieSuggestions(res.data));
        })
        .catch(() => callBack([]));
};

export const getNormalUserSelfiesApi = (token, { page = 1, perPage = 100 } = {}, callBack, error) => {
    paginatedGet(`${APIS.GET_NORMAL_USER_SELFIES}?page=${page}&perPage=${perPage}`, token, callBack, error, normalizeSelfieList);
};

export const getOwnSelfiesApi = (token, { page = 1, perPage = 10 } = {}, callBack, error) => {
    paginatedGet(`${APIS.GET_OWN_SELFIES}?page=${page}&perPage=${perPage}`, token, callBack, error, normalizeSelfieList);
};

export const getLikedSelfiesApi = (token, { page = 1, perPage = 10 } = {}, callBack, error) => {
    paginatedGet(`${APIS.GET_LIKED_SELFIES}?page=${page}&perPage=${perPage}`, token, callBack, error, normalizeSelfieList);
};

export const getSelfieApi = (token, selfieId, callBack, error) => {
    entityGet(`${APIS.GET_SELFIE}${selfieId}`, token, callBack, error, normalizeSelfieItem);
};

export const submitSelfieApi = (token, data, callBack, error) => {
    axios.post(APIS.SUBMIT_SELFIE, data, authHeaders(token))
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const fileUploadApi = (token, file, fileType = 'selfies', callBack, error) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);

    axios.post(APIS.FILE_UPLOAD, formData, {
        headers: {
            Authorization: token,
            'Content-Type': 'multipart/form-data',
        },
    })
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const getUploadedFileUrl = extractUploadedFileUrl;

export const updateProfileImageApi = (token, profileImage, callBack, error) => {
    axios.put(APIS.UPDATE_PROFILE_IMAGE, { profile_image: profileImage }, authHeaders(token))
        .then((res) => {
            if (isApiSuccess(res.data)) {
                notifyProfileUpdated({
                    ...(getStoredUser() || {}),
                    profile_image: profileImage,
                    picture: profileImage,
                });
            }
            if (callBack) callBack(res.data);
        })
        .catch(handleAuthError(callBack, error));
};

export const editProfileApi = (token, data, callBack, error) => {
    axios.put(APIS.EDIT_PROFILE, data, authHeaders(token))
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const completeEsignApi = (token, esignFileUrl, callBack, error) => {
    axios.put(APIS.COMPLETE_ESIGN, { esign_file_url: esignFileUrl }, authHeaders(token))
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const getUserProfileStatsApi = (token, userId, callBack, error) => {
    entityGet(
        `${APIS.USER_PROFILE_STATS}${userId}`,
        token,
        callBack,
        error,
        normalizeUserProfileStats
    );
};

export const getUserSelfiesApi = (token, userId, { page = 1, perPage = 12 } = {}, callBack, error) => {
    paginatedGet(`${APIS.USER_SELFIES}${userId}?page=${page}&perPage=${perPage}`, token, callBack, error, normalizeSelfieList);
};

export const uploadProfileImageApi = (token, file, callBack, error) => {
    fileUploadApi(token, file, 'profile-image', (uploadRes) => {
        if (!isApiSuccess(uploadRes)) {
            callBack({ success: false, message: getApiMessage(uploadRes, 'Upload failed.') });
            return;
        }
        const profileImage = extractUploadedFileUrl(uploadRes);
        if (!profileImage) {
            callBack({ success: false, message: 'Upload succeeded but no file URL was returned.' });
            return;
        }
        updateProfileImageApi(token, profileImage, callBack, error);
    }, error);
};

const mapToEditProfilePayload = (fields) => {
    const payload = {};
    const fieldMap = {
        first_name: 'first_name',
        last_name: 'last_name',
        username: 'username',
        email: 'email',
        profile_image: 'profile_image',
        picture: 'profile_image',
        dob: 'dob',
        birthdate: 'dob',
        gender: 'gender',
        bio: 'bio',
        instagram_link: 'instagram_link',
    };

    Object.entries(fields).forEach(([key, value]) => {
        const mappedKey = fieldMap[key];
        if (mappedKey && value !== undefined && value !== '') {
            payload[mappedKey] = value;
        }
    });

    return payload;
};

// Backward-compatible wrappers
export const creatorApi = (
    token,
    {
        page = '',
        perPage = 100,
        search = '',
        searchTerm = '',
        user_id = '',
        brand_id = '',
        userId = '',
        brandId = '',
    } = {},
    callBack,
    error
) => {
    getCreatorSelfiesApi(
        token,
        {
            page: toApiPage(page),
            perPage,
            search: search || searchTerm,
            user_id: user_id || userId,
            brand_id: brand_id || brandId,
        },
        callBack,
        error
    );
};

export const dicoverApi = (token, { page, perPage = 100 } = {}, callBack, error) => {
    getNormalUserSelfiesApi(token, { page: toApiPage(page), perPage }, callBack, error);
};

export const getSelfieByIdNew = (token, id, callBack, error) => getSelfieApi(token, id, callBack, error);

export const getSelfieById = (token, id, callBack, error) => getSelfieApi(token, id, callBack, error);

export const getItemDataById = (token, id, callBack, error) => getProductApi(token, id, callBack, error);

export const addToCartApi = (token, data, callBack, error) => {
    let payload = data;

    if (Array.isArray(data)) {
        const item = data[0] || {};
        payload = {
            affiliate_id: item.affiliate_id || getSelfieAffiliateId(item) || getAffiliateId(),
            product_id: item.product_id || item.item_id,
            variant_id: item.variant_id || item.size,
            quantity: item.quantity || 1,
        };
    }

    axios.post(APIS.ADD_TO_CART, payload, authHeaders(token))
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const getMyCartApi = (token, callBack, error) => {
    axios.get(APIS.GET_MY_CART, authHeaders(token))
        .then((res) => callBack(normalizeCartResponse(res.data)))
        .catch(handleAuthError(callBack, error));
};

export const getCartStatsApi = (token, callBack, error) => {
    axios.get(APIS.GET_CART_STATS, authHeaders(token))
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const removeCartItemApi = (token, cartItemId, quantity = 1, callBack, error) => {
    axios.delete(`${APIS.REMOVE_CART_ITEM}${cartItemId}`, {
        ...authHeaders(token),
        data: { quantity },
    })
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const initiatePaymentApi = (token, addressId, callBack, error) => {
    axios.post(APIS.INITIATE_PAYMENT, { address_id: addressId }, authHeaders(token))
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const getPurchasedProductsApi = (token, { page = 1, perPage = 10 } = {}, callBack, error) => {
    paginatedGet(
        `${APIS.GET_PURCHASED_PRODUCTS}?page=${page}&perPage=${perPage}`,
        token,
        callBack,
        error,
        normalizePurchasedProductList
    );
};

export const getCartDataApi = (token, callBack, error) => getMyCartApi(token, callBack, error);
export const changepasswordApi = (token, id, callBack, error) => {
    const url = `${APIS.CHANGE_PASSWORD_API}`;

    const headerConfig = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `${token}`
        },
    };

    axios.post(url, headerConfig)
        .then((res) => {
            callBack(res.data);
        })
        .catch((err) => {
            console.error("API Error:", err);
            if (err.response) {
                callBack(err.response.data);
            }
        });
};

export const profileWishListApi = (token, callBack, error) => {
    const url = `${APIS.PROFILE_WISH_LIST_API}`;

    const headerConfig = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `${token}`
        },
    };

    axios.get(url, headerConfig)
        .then((res) => {
            callBack(res.data);
        })
        .catch((err) => {
            console.error("API Error:", err);
            if (err.response) {
                callBack(err.response.data);
            }
        });
};

export const productWinApi = (token, id, callBack, error) => {
    const url = `${APIS.PRODUCT_WIN_API}`;

    const headerConfig = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `${token}`
        },
    };

    axios.get(url, headerConfig)
        .then((res) => {
            callBack(res.data);
        })
        .catch((err) => {
            console.error("API Error:", err);
            if (err.response) {
                callBack(err.response.data);
            }
        });
};
export const myPurchaseApi = (token, callBack, error) => {
    axios.get(`${APIS.GET_PURCHASED_PRODUCTS}?page=1&perPage=50`, authHeaders(token))
        .then((res) => {
            const raw = res.data;
            const items = normalizePurchasedProductList(extractApiList(raw));

            callBack({
                ...normalizeApiResponse(raw),
                success: isApiSuccess(raw),
                message: getApiMessage(raw),
                data: items,
            });
        })
        .catch(handleAuthError(callBack, error));
};
export const myImagesInprofile = (token, callBack, error) => {
    getOwnSelfiesApi(token, { page: 1, perPage: 100 }, callBack, error);
};

const deliverSelfieLikeResult = (raw, callBack, action) => {
    const normalized = normalizeSelfieLikeResponse(raw, action);

    if (normalized.success) {
        showAppSnackbar(action === 'like' ? 'Liked' : 'Unliked', 'success');
    } else {
        showAppSnackbar(getApiMessage(normalized, 'Action failed.'), 'error');
    }

    callBack(normalized);
};

const handleSelfieLikeResponse = (callBack, action) => (res) =>
    deliverSelfieLikeResult(res.data, callBack, action);

export const likeSelfieApi = (token, selfieId, callBack, error) => {
    if (!selfieId) {
        const failure = { success: false, message: 'Selfie id is required.' };
        deliverSelfieLikeResult(failure, callBack, 'like');
        return;
    }

    axios.post(`${APIS.LIKE_SELFIE}${selfieId}/like`, {}, authHeaders(token))
        .then(handleSelfieLikeResponse(callBack, 'like'))
        .catch((err) => {
            console.error("API Error:", err);
            deliverSelfieLikeResult(err.response?.data || { success: false, message: err.message }, callBack, 'like');
            if (error) error(err);
        });
};

export const unlikeSelfieApi = (token, selfieId, callBack, error) => {
    if (!selfieId) {
        const failure = { success: false, message: 'Selfie id is required.' };
        deliverSelfieLikeResult(failure, callBack, 'unlike');
        return;
    }

    axios.post(`${APIS.UNLIKE_SELFIE}${selfieId}/unlike`, {}, authHeaders(token))
        .then(handleSelfieLikeResponse(callBack, 'unlike'))
        .catch((err) => {
            console.error("API Error:", err);
            deliverSelfieLikeResult(err.response?.data || { success: false, message: err.message }, callBack, 'unlike');
            if (error) error(err);
        });
};

export const getSelfieLikeApi = likeSelfieApi;
export const getunSelfieLikeApi = unlikeSelfieApi;

const deliverProductLikeResult = (raw, callBack, action) => {
    const normalized = normalizeProductLikeResponse(raw, action);

    if (normalized.success) {
        showAppSnackbar(action === 'like' ? 'Liked' : 'Unliked', 'success');
    } else {
        showAppSnackbar(getApiMessage(normalized, 'Action failed.'), 'error');
    }

    callBack(normalized);
};

const handleProductLikeResponse = (callBack, action) => (res) =>
    deliverProductLikeResult(res.data, callBack, action);

export const likeProductApi = (token, variantId, callBack, error) => {
    if (!variantId) {
        const failure = { success: false, message: 'Product variant id is required.' };
        deliverProductLikeResult(failure, callBack, 'like');
        return;
    }

    axios.post(`${APIS.LIKE_PRODUCT}${variantId}/like`, {}, authHeaders(token))
        .then(handleProductLikeResponse(callBack, 'like'))
        .catch((err) => {
            console.error("API Error:", err);
            deliverProductLikeResult(err.response?.data || { success: false, message: err.message }, callBack, 'like');
            if (error) error(err);
        });
};

export const unlikeProductApi = (token, variantId, callBack, error) => {
    if (!variantId) {
        const failure = { success: false, message: 'Product variant id is required.' };
        deliverProductLikeResult(failure, callBack, 'unlike');
        return;
    }

    axios.post(`${APIS.UNLIKE_PRODUCT}${variantId}/unlike`, {}, authHeaders(token))
        .then(handleProductLikeResponse(callBack, 'unlike'))
        .catch((err) => {
            console.error("API Error:", err);
            deliverProductLikeResult(err.response?.data || { success: false, message: err.message }, callBack, 'unlike');
            if (error) error(err);
        });
};

export const getProfileDataApi = (token, userId, page, callBack, error) => {
    getUserSelfiesApi(token, userId, { page: toApiPage(page), perPage: 12 }, callBack, error);
};

export const getLikedDataApi = (token, callBack, error) => {
    getLikedSelfiesApi(token, { page: 1, perPage: 100 }, callBack, error);
};
export const getWinDataApi = (token, callBack, error) => {
    const url = `${APIS.GET_WIN_DATA}`;

    const headerConfig = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `${token}`
        },
    };

    axios.get(url, headerConfig)
        .then((res) => {
            callBack(res.data);
        })
        .catch((err) => {
            console.error("API Error:", err);
            if (err.response) {
                callBack(err.response.data);
            }
        });
};

export const getFollowcountApi = (token, userId, callBack, error) => {
    getUserProfileStatsApi(
        token,
        userId,
        (stats) => callBack(stats || { follows: 0, is_following: false }),
        error
    );
};

export const followUserApi = (token, followingId, _followerId, callback) => {
    axios.post(APIS.FOLLOW_USER, { following_id: followingId }, authHeaders(token))
        .then((res) => callback(res.data))
        .catch((err) => {
            console.error("Follow error:", err);
            callback(err.response?.data || { success: false, message: err.message });
        });
};

export const unfollowUserApi = (token, followingId, callback) => {
    axios.delete(APIS.UNFOLLOW_USER, {
        ...authHeaders(token),
        data: { following_id: followingId },
    })
        .then((res) => callback(res.data))
        .catch((err) => {
            console.error("Unfollow error:", err);
            callback(err.response?.data || { success: false, message: err.message });
        });
};

export const getAddressDetailsApi = (token, addressId, callBack, error) => {
    entityGet(`${APIS.GET_ADDRESS}${addressId}`, token, callBack, error);
};

export const getAllAddressesApi = (token, { page = 1, perPage = 10 } = {}, callBack, error) => {
    paginatedGet(`${APIS.GET_ALL_ADDRESSES}?page=${page}&perPage=${perPage}`, token, callBack, error);
};

export const addAddressApi = (token, data, callBack, error) => {
    axios.post(APIS.ADD_ADDRESS, data, authHeaders(token))
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const editAddressDetailsApi = (token, addressId, data, callBack, error) => {
    axios.put(`${APIS.EDIT_ADDRESS}${addressId}`, data, authHeaders(token))
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const deleteAddressApi = (token, addressId, callBack, error) => {
    axios.delete(`${APIS.DELETE_ADDRESS}${addressId}`, authHeaders(token))
        .then(handleAuthResponse(callBack, error))
        .catch(handleAuthError(callBack, error));
};

export const syncDefaultAddressId = (addresses) => {
    const list = Array.isArray(addresses) ? addresses : [];
    const defaultAddress = list.find((a) => a.is_default) || list[0];
    if (defaultAddress?._id || defaultAddress?.id) {
        localStorage.setItem('addressId', defaultAddress._id || defaultAddress.id);
    }
};

export const getUserAddressApi = getAddressDetailsApi;

export const updateUserProfileApi = (token, _id, updatedFields, callback, error) => {
    const payload = mapToEditProfilePayload(updatedFields);
    if (Object.keys(payload).length === 0) {
        callback({ success: false, message: 'No valid profile fields to update.' });
        return;
    }
    editProfileApi(token, payload, callback, error);
};
export const getNoticationsApi = (token, callBack, error) => {
    axios.get(APIS.GET_NOTIFICATION, authHeaders(token))
        .then((res) => {
            const payload = res.data;
            const list = extractApiList(payload);
            const notifications = list.length
                ? list
                : (Array.isArray(payload?.notifications) ? payload.notifications : []);
            callBack(notifications);
        })
        .catch((err) => {
            console.error("API Error:", err);
            if (error) error(err);
            callBack([]);
        });
};
export const deleteNotificationApi = async (id, token, callback) => {
    try {
      const response = await axios.delete(`${BASE_URL}notifications/user`, {
        data: { id },
        headers: {
          Authorization: token,
          "Content-Type": "application/json"
        }
      });
      callback(response.data);
    } catch (error) {
      callback({ success: false, error: error.message });
    }
  };
  
  export const getNotificationDetailApi = async (id, token, callback) => {
    try {
      const response = await axios.get(`${BASE_URL}notifications/${id}`, {
        headers: {
          Authorization: token,
        }
      });
      callback(response.data);
    } catch (error) {
      callback({ success: false, error: error.message });
    }
  };
  